import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { boardGroupsService } from '../api/board-groups.service';
import { BOARD_KEYS } from './queryKeys';
import { TASK_KEYS } from '@/features/tasks/public';
import type {
  CreateBoardGroupPayload,
  UpdateBoardGroupPayload,
  ReorderTaskGroupsRequest,
} from '../types';

export function useCreateBoardGroup(boardId: string) {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);

  return useMutation({
    mutationFn: (data: CreateBoardGroupPayload) => boardGroupsService.create(boardId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: BOARD_KEYS.groups(orgId ?? '', boardId),
      });
    },
  });
}

export function useUpdateBoardGroup(boardId: string) {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);

  return useMutation({
    mutationFn: ({ groupId, data }: { groupId: string; data: UpdateBoardGroupPayload }) =>
      boardGroupsService.update(boardId, groupId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: BOARD_KEYS.groups(orgId ?? '', boardId),
      });
    },
  });
}

export function useDeleteBoardGroup(boardId: string, projectId: string) {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);

  return useMutation({
    mutationFn: (groupId: string) => boardGroupsService.delete(boardId, groupId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: BOARD_KEYS.groups(orgId ?? '', boardId),
      });
      queryClient.invalidateQueries({
        queryKey: TASK_KEYS.projectRoot(orgId ?? '', projectId),
      });
    },
  });
}

export function useReorderBoardGroups(boardId: string) {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);

  return useMutation({
    mutationFn: (data: ReorderTaskGroupsRequest) => boardGroupsService.reorder(boardId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: BOARD_KEYS.groups(orgId ?? '', boardId),
      });
    },
  });
}
